'use client'

import { useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useDeleteSchedule } from '@/core/hooks/useSchedule'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { LogoIcon } from '@/shared/components/atoms/Logo'
import { Separator } from '@/shared/components/atoms/Separator'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

interface RemoveScheduleModalProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	scheduleId?: string
	scheduleName?: string
	scheduleDescription?: string
	onRemoved?: () => void
}

export const RemoveScheduleModal = ({
	open,
	onOpenChange,
	scheduleId,
	scheduleName,
	scheduleDescription,
	onRemoved,
}: RemoveScheduleModalProps) => {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const { showNotification } = useNotificationContext()
	const [isRemoving, setIsRemoving] = useState(false)

	const { mutateAsync: deleteSchedule } = useDeleteSchedule({
		config: useAccountConfig({}),
		options: {
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: ['schedules-assigned'] })
				queryClient.invalidateQueries({ queryKey: ['schedules'] })
			},
		},
	})

	const handleRemove = async () => {
		if (!scheduleId) return

		setIsRemoving(true)

		try {
			await deleteSchedule(scheduleId)

			showNotification({
				type: 'success',
				message: t('entities.schedule.notifications.removed'),
				description: t('entities.schedule.notifications.removed_successfully', {
					name: scheduleName,
				}),
			})

			onOpenChange(false)
			onRemoved?.()
		} catch {
			showNotification({
				type: 'error',
				message: t('entities.schedule.notifications.failed_to_remove'),
				description: t(
					'entities.schedule.notifications.please_try_again_later'
				),
			})
		} finally {
			setIsRemoving(false)
		}
	}

	const truncatedDescription = scheduleDescription
		? scheduleDescription.length > 150
			? `${scheduleDescription.substring(0, 150)}...`
			: scheduleDescription
		: ''

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl p-0 gap-0">
				<DialogHeader className="px-6 pt-6 pb-3">
					<DialogTitle className="text-2xl font-semibold">
						{t('entities.schedule.remove.title')}
					</DialogTitle>
				</DialogHeader>

				<Separator />

				<div className="px-6 py-6 space-y-6">
					<p className="text-base text-muted-foreground">
						{t('entities.schedule.remove.description')}
					</p>

					<div className="space-y-4">
						<div className="relative aspect-video rounded-2xl bg-gradient-to-br from-zinc-300 to-zinc-400 dark:from-zinc-700 dark:to-zinc-800 overflow-hidden flex items-center justify-center">
							<div className="absolute inset-0 flex items-center justify-center">
								<LogoIcon width="80px" height="80px" />
							</div>
						</div>

						<div className="space-y-2">
							<h3 className="text-xl font-semibold">{scheduleName}</h3>
							{truncatedDescription && (
								<p className="text-sm text-muted-foreground line-clamp-3">
									{truncatedDescription}
								</p>
							)}
						</div>
					</div>
				</div>

				<Separator />

				<DialogFooter className="px-6 py-4">
					<Button
						type="button"
						variant="outline"
						onClick={() => onOpenChange(false)}
						className="h-9 rounded-full px-6"
						disabled={isRemoving}
					>
						{t('actions.cancel')}
					</Button>
					<Button
						type="button"
						variant="destructive"
						onClick={handleRemove}
						disabled={isRemoving}
						className="h-9 rounded-full px-6"
					>
						{isRemoving
							? t('entities.schedule.remove.removing')
							: t('entities.schedule.remove.remove')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
